import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useCustomerMap, useMap, useRunGeocoding } from '../api/queries'
import type { MapPoint } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'

/** Midt i Danmark - vises kun, hvis der ikke er et eneste punkt at zoome til. */
const DENMARK: [number, number] = [56.1, 10.2]

/**
 * Sagerne på et kort.
 *
 * Punkterne kommer fra kundens adresse, slået op i Danmarks adresseregister.
 * Kunder uden adresse - eller med en adresse, der ikke kunne bestemmes - får
 * intet punkt, og antallet står i stedet øverst: et kort, der viser otte ud af
 * tolv sager, ser ellers ud, som om der kun er otte.
 *
 * Baggrundsfliserne kommer fra OpenStreetMap. Valgt frem for Google Maps, fordi
 * det er gratis og ikke kræver en nøgle, der skal passes på - og fordi kortet
 * kun skal vise, hvor tingene ligger i forhold til hinanden.
 */
export function MapPage() {
  // Kortet holdes i state frem for i en ref: en ref udløser ingen ny render, og
  // så ville zoomet afhænge af, om kortet eller data kom først.
  const [map, setMap] = useState<L.Map | null>(null)
  const [includeService, setIncludeService] = useState(true)
  // Sager (det der er noget at lave ved) eller ALLE kunder og enheder (beslutning 109).
  const [mode, setMode] = useState<'projects' | 'customers'>('projects')
  const projectMap = useMap(includeService)
  const customerMap = useCustomerMap(mode === 'customers')
  const { data, isLoading, error } = mode === 'customers' ? customerMap : projectMap
  const { isAdmin } = useAuth()
  const geocode = useRunGeocoding()
  const [geocodeMessage, setGeocodeMessage] = useState<string | null>(null)

  const points = data?.points ?? []

  // Zoom til det, der faktisk er - ikke til et fast udsnit af Danmark. Ligger
  // alle sager i Nordjylland, skal kortet vise Nordjylland.
  //
  // Nøglen er koordinaterne og ikke selve listen: `points` er en ny reference
  // ved hver render, og så ville grænserne blive regnet forfra i det uendelige.
  const key = points.map((p) => `${p.latitude},${p.longitude}`).join('|')
  const bounds = useMemo(
    () => (key === '' ? null : L.latLngBounds(key.split('|').map((c) => c.split(',').map(Number) as [number, number])).pad(0.15)),
    [key]
  )

  // Zoom ud til alle punkterne - men først når kortet faktisk HAR en størrelse.
  //
  // Leaflet måler sin container, når kortet oprettes. Sker det, mens fanen er
  // skjult eller ruden endnu ikke er tegnet, måler den bredden til nul - og
  // uden en bredde kan der ikke regnes et zoomniveau ud. Kortet lander på
  // zoom 0, altså hele verden i én flise, med alle danske punkter oven i
  // hinanden. Derfor ventes der på, at containeren får plads.
  useEffect(() => {
    if (!map || !bounds) return

    const element = map.getContainer()
    let fitted = false

    const apply = () => {
      if (element.clientWidth === 0 || element.clientHeight === 0) return

      map.invalidateSize()

      // Kun første gang: senere størrelsesændringer (vinduet trækkes, menuen
      // foldes ud) må ikke rive brugeren ud af det sted, han selv har zoomet til.
      if (!fitted) {
        map.fitBounds(bounds, { maxZoom: 12 })
        fitted = true
      }
    }

    apply()

    const observer = new ResizeObserver(apply)
    observer.observe(element)
    return () => observer.disconnect()
  }, [map, bounds])

  async function runGeocoding() {
    setGeocodeMessage(null)
    try {
      const r = await geocode.mutateAsync()
      setGeocodeMessage(
        r.found === 0 && r.remaining === 0
          ? 'Alle adresser er slået op.'
          : `Slog ${r.found} ${r.found === 1 ? 'adresse' : 'adresser'} op.` +
            (r.remaining > 0 ? ` ${r.remaining} mangler stadig - se listen nederst.` : '')
      )
    } catch (e) {
      setGeocodeMessage(e instanceof Error ? e.message : 'Adresseopslaget kunne ikke køres.')
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />

  // Det brugeren skal GOERE noget ved. Dem der bare venter paa opslaget staar
  // for sig - de kraever ingen handling, og at kalde dem "mangler" ville sende
  // folk ud at lede efter en fejl, der ikke er der.
  const missing = (data?.missingAddress ?? 0) + (data?.missingPosition ?? 0)
  const awaiting = data?.awaitingLookup ?? 0

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-title">
          {points.length} {points.length === 1 ? 'punkt' : 'punkter'} på kortet
        </div>
        <select
          style={{ width: 'auto' }}
          value={mode}
          onChange={(e) => setMode(e.target.value as 'projects' | 'customers')}
        >
          <option value="projects">Sager</option>
          <option value="customers">Kunder og enheder</option>
        </select>
        {mode === 'projects' && (
          <label className="cal-person" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={includeService} onChange={(e) => setIncludeService(e.target.checked)} />
            <span>Vis service der forfalder</span>
          </label>
        )}
        {isAdmin && (
          <button className="btn btn--sm" disabled={geocode.isPending} onClick={runGeocoding}>
            {geocode.isPending ? 'Slår op ...' : 'Slå adresser op nu'}
          </button>
        )}
      </div>

      {geocodeMessage && <div className="small" style={{ marginBottom: 10 }}>{geocodeMessage}</div>}

      {/* En adresse, der lige er sat, er ikke slået op endnu - baggrundsjobbet
          kigger hvert kvarter. Det ER ikke en fejl, og det skal ikke stå i den
          røde boks: bliver folk bedt om at rette en adresse, der fejler
          ingenting, holder de op med at tro på beskeden. */}
      {awaiting > 0 && (
        <div className="map-notice">
          <strong>
            {awaiting === 1
              ? 'Én adresse er ved at blive slået op.'
              : `${awaiting} adresser er ved at blive slået op.`}
          </strong>{' '}
          Punktet kommer af sig selv - som regel inden for et kvarter.
          {isAdmin && ' Vil du ikke vente, så tryk "Slå adresser op nu".'}
        </div>
      )}

      {/* Hullerne skal siges højt. Ellers ser kortet komplet ud, selv om der
          mangler sager på det. */}
      {missing > 0 && (
        <div className="map-warning">
          <strong>
            {missing}{' '}
            {mode === 'customers'
              ? missing === 1 ? 'kunde mangler' : 'kunder mangler'
              : missing === 1 ? 'sag mangler' : 'sager mangler'}{' '}
            på kortet.
          </strong>{' '}
          {(data?.missingAddress ?? 0) > 0 && (
            <>
              {data!.missingAddress} {mode === 'customers' ? 'er' : 'har en kunde'} helt uden adresse.{' '}
            </>
          )}
          {(data?.missingPosition ?? 0) > 0 && (
            <>
              {data!.missingPosition} har en adresse, der <strong>blev slået op og ikke kunne
              bestemmes</strong> - typisk en vej eller et husnummer, der ikke findes, eller flere
              adresser der passer. Ret adressen på kunden eller på selve sagen, så kommer punktet
              af sig selv. Sagen åbner sit eget felt under &quot;Arbejdet foregår&quot;.
            </>
          )}
        </div>
      )}

      <Card>
        {points.length === 0 ? (
          <EmptyState>
            {awaiting > 0
              ? 'Der er ingen punkter at vise endnu - adresserne er ved at blive slået op.'
              : mode === 'customers'
                ? 'Der er ingen kunder med en adresse på kortet endnu.'
                : 'Der er ingen punkter at vise. Enten er der ingen åbne sager, eller også mangler kunderne en adresse.'}
          </EmptyState>
        ) : (
          <div className="map-frame">
            <MapContainer
              center={DENMARK}
              zoom={7}
              ref={setMap}
              scrollWheelZoom
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                // OpenStreetMap kræver, at der står hvor fliserne kommer fra.
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {points.map((p) => (
                <Marker key={`${p.kind}-${p.id}`} position={[p.latitude, p.longitude]} icon={iconFor(p)}>
                  <Popup>
                    <PointPopup point={p} />
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        )}
      </Card>

      {points.length > 0 && <PointList points={points} />}
    </>
  )
}

/**
 * Markøren tegnes selv frem for at bruge Leaflets standardbillede: det ligger
 * som en billedfil, der skal findes på serveren, og det er en klassisk kilde
 * til et kort med usynlige markører efter en udrulning.
 */
/** Hvor et punkt fører hen: sagen, enheden eller kunden. */
function linkFor(point: MapPoint) {
  if (point.kind === 'customer') return `/customers/${point.id}`
  if (point.kind === 'service' || point.kind === 'device') return `/devices/${point.id}`
  return `/projects/${point.id}`
}

function iconFor(point: MapPoint) {
  const color = point.isOverdue ? '#DC2626' : point.statusColor
  const ring = point.kind === 'service' || point.kind === 'device' ? 'stroke-dasharray="3 2"' : ''

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
      <path d="M13 33C13 33 25 20.5 25 13A12 12 0 1 0 1 13c0 7.5 12 20 12 20z"
            fill="${color}" stroke="#fff" stroke-width="2" ${ring}/>
      <circle cx="13" cy="13" r="4.5" fill="#fff"/>
    </svg>`

  return L.divIcon({
    html: svg,
    className: 'map-pin',
    iconSize: [26, 34],
    iconAnchor: [13, 33],
    popupAnchor: [0, -30]
  })
}

function PointPopup({ point }: { point: MapPoint }) {
  return (
    <div className="map-popup">
      <Link to={linkFor(point)}>
        <strong>{point.name}</strong>
      </Link>
      {point.customerName && point.kind !== 'customer' && <div>{point.customerName}</div>}
      {point.matchedAddress && <div className="muted">{point.matchedAddress}</div>}
      <div>
        {point.statusName}
        {point.dueDate && (
          <>
            {' · '}
            <span className={point.isOverdue ? 'overdue' : ''}>
              {point.isOverdue ? 'overskredet ' : 'frist '}
              {formatDate(point.dueDate)}
            </span>
          </>
        )}
      </div>
      {point.assignedUserNames.length > 0 ? (
        <div className="muted">På sagen: {point.assignedUserNames.join(', ')}</div>
      ) : (
        point.kind === 'project' && <div className="muted">Ingen er sat på endnu</div>
      )}
    </div>
  )
}

/** Listen under kortet: den er hurtigere at skimme end at klikke sig gennem nåle. */
function PointList({ points }: { points: MapPoint[] }) {
  return (
    <Card title="Alle punkter">
      <div className="table-wrap">
        <table className="cal-table">
          <thead>
            <tr>
              <th>Hvad</th>
              <th>Kunde</th>
              <th>Adresse</th>
              <th>Status</th>
              <th>Frist</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={`${p.kind}-${p.id}`}>
                <td>
                  <Link to={linkFor(p)}>{p.name}</Link>
                  {p.kind === 'service' && <span className="cal-chip__service" style={{ marginLeft: 6 }}>Service</span>}
                </td>
                <td>{p.customerName ?? '-'}</td>
                <td className="small muted">{p.matchedAddress ?? p.address ?? '-'}</td>
                <td>
                  <span className="pill" style={{ background: p.statusColor }}>{p.statusName}</span>
                </td>
                <td className={p.isOverdue ? 'overdue' : ''}>{p.dueDate ? formatDate(p.dueDate) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function formatDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
}
